import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, MapPin, Plus, AlertCircle, Archive as ArchiveIcon, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PM_ADMIN_ROLES = ['property_manager', 'asset_manager', 'maintenance_supervisor'];

const Properties = () => {
  const { user } = useAuth();
  const isAdmin = PM_ADMIN_ROLES.includes(user?.role);
  const [list, setList] = useState([]);
  const [err, setErr] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', city: '', state: '', zip: '', unit_count: 0, gate_code: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/pm/properties`, { withCredentials: true });
      setList(Array.isArray(data) ? data : (data.items || []));
      setErr('');
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pm/properties?include_archived=true`, { withCredentials: true });
        if (!alive) return;
        setList(Array.isArray(data) ? data : (data.items || []));
        setErr('');
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || e.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setBusy(true);
    try {
      const body = { ...form, unit_count: Number(form.unit_count) || 0 };
      Object.keys(body).forEach((k) => { if (typeof body[k] === 'string') body[k] = body[k].trim(); });
      await axios.post(`${API}/pm/properties`, body, { withCredentials: true });
      toast.success(`Property "${body.name}" created`);
      setOpen(false);
      setForm({ name: '', address: '', city: '', state: '', zip: '', unit_count: 0, gate_code: '' });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join(' · ') : (d?.code || d || e.message);
      toast.error(`Could not create: ${msg}`);
    }
    setBusy(false);
  };

  const visible = includeArchived ? list : list.filter((p) => (p.archive_status || 'active') !== 'archived');

  return (
    <div data-testid="pm-properties-page" className="p-6 space-y-6 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTIES</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Your Properties</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">{visible.length} {visible.length === 1 ? 'property' : 'properties'}{isAdmin ? ' in your portfolio' : ' assigned to you'}.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Link to="/pm/bulk-import" data-testid="bulk-import-link">
              <Button variant="outline" className="border-[#404040] text-white"><Upload className="w-4 h-4 mr-1" /> Bulk Import</Button>
            </Link>
            <Button data-testid="new-property-btn" onClick={() => setOpen(true)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]">
              <Plus className="w-4 h-4 mr-1" /> New Property
            </Button>
          </div>
        )}
      </div>

      {isAdmin && (
        <label className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] cursor-pointer">
          <input data-testid="toggle-archived" type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Show archived
        </label>
      )}

      {err && <p data-testid="pm-properties-error" className="text-[#EF4444]">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {visible.map((p) => {
          const isArchived = (p.archive_status || 'active') === 'archived';
          return (
            <Link
              key={p.id}
              to={`/pm/properties/${p.id}`}
              data-testid={`pm-property-card-${p.id}`}
              className={`bg-[#141414] border ${isArchived ? 'border-[#404040] opacity-60' : 'border-[#262626] hover:border-[#D4AF37]'} transition-colors rounded-md p-5 block`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded bg-[#D4AF37]/10 flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-[#D4AF37]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate flex items-center gap-2">
                    {p.name}
                    {isArchived && <ArchiveIcon className="w-3.5 h-3.5 text-[#A3A3A3]" title="Archived" />}
                  </p>
                  <p className="text-[#525252] text-xs font-mono">{p.property_code}</p>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm">
                <p className="text-[#A3A3A3] flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#525252]" /> {p.address}{p.city ? `, ${p.city}` : ''}{p.state ? `, ${p.state}` : ''}{p.zip ? ` ${p.zip}` : ''}</p>
                <p className="text-[#525252] text-xs">{p.unit_count || 0} units</p>
              </div>
            </Link>
          );
        })}
        {visible.length === 0 && !err && (
          <div className="text-[#525252] col-span-full p-8 text-center bg-[#141414] border border-[#262626] rounded-md">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-[#525252]" />
            <p>No properties yet.</p>
            {isAdmin && <p className="text-xs mt-1">Click &quot;New Property&quot; to add one.</p>}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>New Property</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">Saved immediately to your portfolio. You can edit any field later from the property page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Property name *</Label>
              <Input data-testid="prop-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Street address</Label>
              <Input data-testid="prop-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label>City</Label>
                <Input data-testid="prop-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label>State</Label>
                <Input data-testid="prop-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>ZIP</Label>
                <Input data-testid="prop-zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label># Units</Label>
                <Input data-testid="prop-unit-count" type="number" value={form.unit_count} onChange={(e) => setForm({ ...form, unit_count: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
              <div>
                <Label>Gate code</Label>
                <Input data-testid="prop-gate-code" value={form.gate_code} onChange={(e) => setForm({ ...form, gate_code: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="create-property-submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" disabled={busy || !form.name} onClick={create}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Properties;
